import { useState, useEffect } from "react";
import { Phone, Shield, Clock, Sparkles, Award, Users, CheckCircle, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { trackCallEvent } from "@/lib/analytics";
import { Button } from "@/components/ui/button";
import Layout from "@/components/layout/Layout";
import ServicesCarousel from "@/components/ServicesCarousel";
import PaintBrushDivider from "@/components/PaintBrushDivider";
import { ScrollFade } from "@/hooks/useScrollFade";
import heroImage from "@/assets/hero-home.webp";
import heroShapePainting from "@/assets/hero-home-1.webp";
import heroShapeElectrical from "@/assets/hero-home-2.webp";
import heroShapeRemodeling from "@/assets/hero-home-3.webp";
import heroShapeExterior from "@/assets/hero-home-4.webp";
import lightstimGlow from "@/assets/lightstim-glow.webp";
import spaFaceMask from "@/assets/spa-face-mask.webp";
import awardTop10 from "@/assets/award-top10.webp";

const services = [{
  title: "Signature Facials",
  description: "Cleanse, peel, extract, hydrate, protect, and oxygenate for radiant, glowing skin.",
  icon: "✨"
}, {
  title: "Chemical Peels",
  description: "Medical-grade peels for skin brightening, smoothing, and collagen rejuvenation.",
  icon: "🧪"
}, {
  title: "Dream Lift",
  description: "Non-invasive skin tightening that rebuilds collagen and lasts up to 3 years.",
  icon: "💎"
}, {
  title: "Micro-Needling",
  description: "Powerful wrinkle treatment with collagen stimulation for youthful skin.",
  icon: "🪡"
}, {
  title: "Dermaplaning",
  description: "Custom enzyme peel with dermaplaning for smooth, luminous complexion.",
  icon: "🌸"
}, {
  title: "Acupuncture",
  description: "Cosmetic facial acupuncture to firm skin and minimize fine lines and wrinkles.",
  icon: "🧘"
}];

const trustBadges = [{
  icon: Award,
  label: "Award-Winning"
}, {
  icon: Clock,
  label: "25+ Years Experience"
}, {
  icon: Shield,
  label: "Licensed & Certified"
}, {
  icon: Sparkles,
  label: "Medical Grade"
}, {
  icon: Users,
  label: "Personalized Care"
}];

// Hero images array for mobile carousel
const heroImages = [
  { src: heroShapePainting, gradient: "conic-gradient(from 180deg at 50% 50%, #4ade80 0deg, #2dd4bf 90deg, #f97316 180deg, #ef4444 270deg, #4ade80 360deg)", imagePosition: "center center" },
  { src: heroShapeElectrical, gradient: "conic-gradient(from 90deg at 50% 30%, #c084fc 0deg, #f472b6 120deg, #3b82f6 240deg, #c084fc 360deg)", imagePosition: "70% 30%" },
  { src: heroShapeRemodeling, gradient: "conic-gradient(from 0deg at 50% 50%, #fbbf24 0deg, #f97316 120deg, #3b82f6 240deg, #fbbf24 360deg)", imagePosition: "center center" },
  { src: heroShapeExterior, gradient: "conic-gradient(from 45deg at 50% 50%, #fb7185 0deg, #f43f5e 120deg, #ef4444 240deg, #fb7185 360deg)", imagePosition: "center center" },
];

// Floating gradient shape component with image overlay
interface GradientShapeProps {
  className: string;
  gradient: string;
  image: string;
  animationDelay?: string;
  imagePosition?: string;
}
const GradientShape = ({
  className,
  gradient,
  image,
  animationDelay = "0s",
  imagePosition = "center center"
}: GradientShapeProps) => <div className={`absolute overflow-hidden ${className}`} style={{
  animation: `float 6s ease-in-out infinite`,
  animationDelay
}}>
    <img src={image} alt="" width={900} height={1200} decoding="async" className="absolute inset-0 w-full h-full object-cover" style={{ objectPosition: imagePosition }} />
    <div className="absolute inset-0" style={{
    background: gradient,
    mixBlendMode: 'soft-light',
    opacity: 0.7
  }} />
  </div>;

// Desktop gradient shapes - hidden on mobile
const GradientShapesDesktop = () => <div className="absolute inset-0 overflow-hidden pointer-events-none hidden md:flex items-center justify-center">
    <GradientShape className="left-[20%] top-[15%] w-44 h-56 rounded-[2rem]" gradient={heroImages[0].gradient} image={heroImages[0].src} imagePosition={heroImages[0].imagePosition} animationDelay="0s" />
    <GradientShape className="left-[35%] top-[5%] w-36 h-72 rounded-full" gradient={heroImages[1].gradient} image={heroImages[1].src} imagePosition={heroImages[1].imagePosition} animationDelay="1s" />
    <GradientShape className="left-[50%] top-[18%] w-32 h-64 rounded-full" gradient={heroImages[2].gradient} image={heroImages[2].src} imagePosition={heroImages[2].imagePosition} animationDelay="2s" />
    <GradientShape className="left-[65%] top-[22%] w-44 h-44 rounded-full" gradient={heroImages[3].gradient} image={heroImages[3].src} imagePosition={heroImages[3].imagePosition} animationDelay="1.5s" />
  </div>;

// Mobile carousel for hero images
const MobileHeroCarousel = () => {
  const [current, setCurrent] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrent(prev => (prev + 1) % heroImages.length);
    }, 3500);
    return () => clearInterval(timer);
  }, []);

  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none md:hidden">
      <div className="absolute inset-0 flex items-start justify-center pt-16">
        {heroImages.map((img, i) => (
          <div
            key={i}
            className="absolute w-48 h-64 rounded-[2rem] overflow-hidden transition-all duration-700 ease-in-out"
            style={{
              opacity: current === i ? 1 : 0,
              transform: current === i ? 'scale(1)' : 'scale(0.9)',
              animation: current === i ? 'float 6s ease-in-out infinite' : undefined,
            }}
          >
            <img src={img.src} alt="" width={600} height={800} fetchPriority={i === 0 ? "high" : "auto"} loading={i === 0 ? "eager" : "lazy"} decoding="async" className="absolute inset-0 w-full h-full object-cover" style={{ objectPosition: img.imagePosition }} />
            <div className="absolute inset-0" style={{
              background: img.gradient,
              mixBlendMode: 'soft-light',
              opacity: 0.7,
            }} />
          </div>
        ))}
      </div>
      {/* Dots indicator */}
      <div className="absolute top-[21rem] left-1/2 -translate-x-1/2 flex gap-2">
        {heroImages.map((_, i) => (
          <div
            key={i}
            className={`w-1.5 h-1.5 rounded-full transition-all duration-300 ${
              current === i ? 'bg-white w-4' : 'bg-white/40'
            }`}
          />
        ))}
      </div>
    </div>
  );
};

// NiceJob Reviews Widget
const NiceJobReviews = () => {
  useEffect(() => {
    const script = document.createElement("script");
    script.src = "https://cdn.nicejob.co/js/sdk.min.js?id=16fbfff1-4a27-4ba8-ae61-4981e7d3cec4";
    script.defer = true;
    document.body.appendChild(script);
    return () => {
      document.body.removeChild(script);
    };
  }, []);

  return <div className="nj-stories" />;
};

const Index = () => {
  return <Layout title="Medical Spa & Aesthetic Treatments in Lake Forest IL" description="Ivana Glow Medical Spa offers professional facials, chemical peels, skin tightening, and rejuvenation treatments in Lake Forest, IL. 25+ years experience. Call (847) 283-0204 to book.">
      {/* JSON-LD Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify({
        "@context": "https://schema.org",
        "@type": "LocalBusiness",
        "name": "Ivana Glow Medical Spa",
        "image": heroImage,
        "telephone": "+1-847-283-0204",
        "email": "szymanski0501@aol.com",
        "address": {
          "@type": "PostalAddress",
          "streetAddress": "225 E Deerpath",
          "addressLocality": "Lake Forest",
          "addressRegion": "IL",
          "postalCode": "60045",
          "addressCountry": "US"
        },
        "openingHours": "By Appointment",
        "areaServed": ["Lake Forest", "North Shore", "Highland Park", "Lake Bluff", "Libertyville"],
        "priceRange": "$$$",
        "url": "https://www.ivanaglowmedicalspa.com"
      })}
      </script>

      {/* Hero Section - Dark with gradient shapes */}
      <section className="relative h-[85vh] flex items-end bg-dark overflow-hidden">
        <GradientShapesDesktop />
        <MobileHeroCarousel />
        
        <div className="container relative z-10 pb-12 md:pb-16 pt-32">
          <div className="max-w-3xl">
            <h1 className="text-base sm:text-lg md:text-xl lg:text-2xl text-white font-light leading-relaxed">
              Ivana Glow Medical Spa is an{" "}
              <span className="underline decoration-1 underline-offset-4">award-winning</span> practice 
              specializing in medical aesthetics, anti-aging facials, and skin rejuvenation. 
              More than a glow — a skin transformation.
            </h1>
          </div>
          
          {/* CTA buttons */}
          <div className="flex gap-4 mt-10">
            <Button asChild size="lg" className="rounded-full bg-primary hover:bg-primary/90 text-primary-foreground border-none transition-all">
              <a href="tel:+18472830204" onClick={trackCallEvent}>
                <Phone className="w-4 h-4 mr-2" />
                Call Now
              </a>
            </Button>
            <Button asChild variant="outline" size="lg" className="rounded-full border-white/30 text-white hover:bg-white hover:text-foreground transition-all">
              <Link to="/services">
                Our Services
              </Link>
            </Button>
          </div>
        </div>
      </section>

      <PaintBrushDivider variant="dark" />

      {/* Services Preview - 3D Carousel */}
      <section className="py-20 md:py-32 bg-gradient-to-b from-background to-muted overflow-hidden">
        <ScrollFade>
          <div className="container mb-12">
            <div className="text-center">
              <span className="text-muted-foreground text-sm tracking-widest uppercase">01 Treatments</span>
              <h2 className="text-3xl md:text-5xl font-light text-foreground mt-4">
                Medical-grade treatments for your skin
              </h2>
            </div>
          </div>
        </ScrollFade>
        
        <ScrollFade delay={200}>
          <ServicesCarousel />
        </ScrollFade>
        
        <ScrollFade delay={400}>
          <div className="container mt-12 text-center">
            <Button asChild variant="ghost" className="text-foreground hover:text-foreground/80 group">
              <Link to="/services" className="flex items-center gap-2">
                View all treatments
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Link>
            </Button>
          </div>
        </ScrollFade>
      </section>

      <PaintBrushDivider variant="light" />

      {/* Why Choose Us - Circular Stats Design */}
      <section className="py-20 md:py-32 bg-background overflow-hidden">
        <div className="container">
          <div className="flex flex-col lg:flex-row gap-12 lg:gap-20 items-center">
            {/* Left side - Title */}
            <ScrollFade direction="left" className="lg:w-1/3">
              <span className="text-muted-foreground text-sm tracking-widest uppercase">02 Why Us</span>
              <h2 className="text-3xl md:text-5xl font-light text-foreground mt-4">
                Why choose Ivana Glow Medical Spa?
              </h2>
              <p className="text-muted-foreground mt-6">
                Art, skill, and compassion — we deliver results-driven treatments with medical-grade protocols.
              </p>
            </ScrollFade>
            
            {/* Right side - Circular container with stats */}
            <ScrollFade direction="right" delay={200} className="lg:w-2/3 relative">
              {/* Large circular background */}
              <div className="relative w-full max-w-xl mx-auto aspect-square rounded-full bg-gradient-to-br from-muted to-card p-8 md:p-12 flex items-center justify-center" style={{
              boxShadow: 'inset 0 0 60px rgba(0,0,0,0.05), 0 0 80px rgba(0,0,0,0.03)'
            }}>
                {/* Stats cards stacked */}
                <div className="flex flex-col gap-3 w-full max-w-sm">
                  {/* Stat Card 1 */}
                  <div className="bg-card border border-border rounded-xl p-4">
                    <p className="text-muted-foreground text-xs md:text-sm mb-2">
                      With 25+ years of experience, we've treated <span className="bg-primary/20 text-primary px-1.5 py-0.5 rounded text-xs">thousands</span> of clients
                    </p>
                    <div className="flex items-end gap-6 md:gap-10">
                      <div>
                        <span className="text-3xl md:text-4xl font-light text-foreground">25+</span>
                        <span className="text-muted-foreground text-xs block text-center">YEARS</span>
                      </div>
                      <div>
                        <span className="text-2xl md:text-3xl font-light text-foreground">3x</span>
                        <span className="text-muted-foreground text-xs block">AWARD</span>
                      </div>
                      <div>
                        <span className="text-2xl md:text-3xl font-light text-foreground">100%</span>
                        <span className="text-muted-foreground text-xs block">CERTIFIED</span>
                      </div>
                    </div>
                  </div>
                  
                  {/* Stat Card 2 */}
                  <div className="bg-card border border-border rounded-xl p-4">
                    <p className="text-muted-foreground text-xs md:text-sm mb-2">
                      Our patients rave about <span className="bg-green-500/20 text-green-700 px-1.5 py-0.5 rounded text-xs">long-lasting</span> results
                    </p>
                    <div className="flex items-end gap-6 md:gap-10">
                      <div>
                        <span className="text-3xl md:text-4xl font-light text-foreground">5.0</span>
                        <span className="text-muted-foreground text-xs block text-center">RATING</span>
                      </div>
                      <div>
                        <span className="text-2xl md:text-3xl font-light text-foreground">3yr</span>
                        <span className="text-muted-foreground text-xs block">RESULTS</span>
                      </div>
                      <div>
                        <span className="text-2xl md:text-3xl font-light text-foreground">Med</span>
                        <span className="text-muted-foreground text-xs block">GRADE</span>
                      </div>
                    </div>
                  </div>
                  
                  {/* Stat Card 3 */}
                  <div className="bg-card border border-border rounded-xl p-4">
                    <p className="text-muted-foreground text-xs md:text-sm mb-2">
                      Non-invasive treatments with <span className="bg-amber-500/20 text-amber-700 px-1.5 py-0.5 rounded text-xs">proven</span> protocols
                    </p>
                    <div className="flex items-end gap-6 md:gap-10">
                      <div>
                        <span className="text-3xl md:text-4xl font-light text-foreground">12+</span>
                        <span className="text-muted-foreground text-xs block text-center">TREATMENTS</span>
                      </div>
                      <div>
                        <span className="text-2xl md:text-3xl font-light text-foreground">Safe</span>
                        <span className="text-muted-foreground text-xs block">NON-INVASIVE</span>
                      </div>
                      <div>
                        <span className="text-2xl md:text-3xl font-light text-foreground">Custom</span>
                        <span className="text-muted-foreground text-xs block">PROTOCOLS</span>
                      </div>
                    </div>
                  </div>
                  
                  {/* Stat Card 4 */}
                  <div className="bg-card border border-border rounded-xl p-4">
                    <p className="text-muted-foreground text-xs md:text-sm mb-2">
                      Serving North Shore & Gold Coast with <span className="bg-purple-500/20 text-purple-700 px-1.5 py-0.5 rounded text-xs">dedication</span>
                    </p>
                    <div className="flex items-end gap-6 md:gap-10">
                      <div>
                        <span className="text-3xl md:text-4xl font-light text-foreground">5+</span>
                        <span className="text-muted-foreground text-xs block text-center">CITIES</span>
                      </div>
                      <div>
                        <span className="text-2xl md:text-3xl font-light text-foreground">Series</span>
                        <span className="text-muted-foreground text-xs block">PACKAGES</span>
                      </div>
                      <div>
                        <span className="text-2xl md:text-3xl font-light text-foreground">Best</span>
                        <span className="text-muted-foreground text-xs block">RESULTS</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </ScrollFade>
          </div>
        </div>
        {/* LightStim Glow Feature */}
        <ScrollFade delay={300}>
          <div className="container mt-20">
            <div className="flex flex-col md:flex-row gap-10 items-center bg-card border border-border rounded-2xl p-8 md:p-12">
              <div className="md:w-2/5 flex-shrink-0">
                <img
                  src={lightstimGlow}
                  alt="LightStim Glow LED light therapy treatment"
                  width={618}
                  height={663}
                  loading="lazy"
                  decoding="async"
                  className="rounded-xl w-full object-cover max-h-96"
                />
              </div>
              <div className="md:w-3/5">
                <h3 className="text-2xl md:text-3xl font-light text-foreground mb-4">
                  LightStim Glow
                </h3>
                <p className="text-muted-foreground leading-relaxed">
                  After each facial treatment, we use LightStim Glow. It uses amber light, deep red, light red and infrared light to penetrate all skin layers at cellular level to increase the oxygenation of the blood which reduces skin inflammation, skin aging, lines, wrinkles, acne and scarring, leaving your skin feeling deeply rejuvenated and healthy. This also stimulates overall wellbeing and sleep.
                </p>
              </div>
            </div>
          </div>
        </ScrollFade>

        {/* Aesthetic Facials Feature */}
        <ScrollFade delay={400}>
          <div className="container mt-12">
            <div className="flex flex-col md:flex-row gap-10 items-center bg-card border border-border rounded-2xl p-8 md:p-12">
              <div className="md:w-2/5 flex-shrink-0">
                <img
                  src={spaFaceMask}
                  alt="Aesthetic facial with clinical extractions and Algo Vital Treatment"
                  width={600}
                  height={600}
                  loading="lazy"
                  decoding="async"
                  className="rounded-xl w-full object-cover max-h-96"
                />
              </div>
              <div className="md:w-3/5">
                <h3 className="text-2xl md:text-3xl font-light text-foreground mb-4">
                  Aesthetic Facials
                </h3>
                <p className="text-muted-foreground leading-relaxed">
                  Aesthetic Facials include clinical extractions of impurities and Algo Vital Treatment for a long lasting, healthy glow.
                </p>
              </div>
            </div>
          </div>
        </ScrollFade>
      </section>

      <PaintBrushDivider variant="light" />

      {/* Award & Video Section */}
      <section className="py-20 md:py-32 bg-background">
        <div className="container">
          <ScrollFade>
            <div className="text-center mb-16">
              <span className="text-muted-foreground text-sm tracking-widest uppercase">03 Recognition</span>
              <h2 className="text-3xl md:text-5xl font-light text-foreground mt-4">
                Top 10 Skin Care Clinic in Lake County
              </h2>
              <p className="text-muted-foreground mt-4 max-w-2xl mx-auto">
                Ivana Glow Medical Spa has been recognized as one of the Top 10 Skin Care Clinics in Lake County, Illinois — powered by Google Reviews.
              </p>
            </div>
          </ScrollFade>

          <ScrollFade delay={200}>
            <div className="flex flex-col md:flex-row gap-10 items-center justify-center">
              {/* Award Photo */}
              <div className="flex-shrink-0">
                <img
                  src={awardTop10}
                  alt="Ivana Glow Medical Spa - Top 10 2026 BusinessRate Award Winner, Skin Care Clinic, Lake County Illinois"
                  width={800}
                  height={1066}
                  loading="lazy"
                  decoding="async"
                  className="rounded-2xl border border-border shadow-lg max-h-[500px] w-auto object-contain"
                />
              </div>
              {/* Award Video */}
              <div className="flex-shrink-0">
                <video
                  controls
                  playsInline
                  preload="none"
                  className="rounded-2xl shadow-lg max-h-[500px] w-auto"
                >
                  <source src="/videos/award-video.mp4" type="video/mp4" />
                  Your browser does not support the video tag.
                </video>
              </div>
            </div>
          </ScrollFade>
        </div>
      </section>

      <PaintBrushDivider variant="light" />

      {/* Reviews Section */}
      <section className="py-20 md:py-32 bg-muted">
        <div className="container">
          <ScrollFade>
            <div className="mb-16">
              <span className="text-muted-foreground text-sm tracking-widest uppercase">04 Testimonials</span>
              <h2 className="text-3xl md:text-5xl font-light text-foreground mt-4">
                What our clients say
              </h2>
            </div>
          </ScrollFade>

          {/* Video Testimonials */}
          <ScrollFade delay={100}>
            <div className="mb-16 grid gap-6 sm:grid-cols-2 max-w-3xl mx-auto">
              {[1, 2].map((n) => (
                <video
                  key={n}
                  controls
                  playsInline
                  preload="none"
                  className="w-full rounded-2xl border border-border shadow-lg"
                  poster={`/videos/client-poster-${n}.webp`}
                >
                  <source src={`/videos/client-video-${n}.mp4`} type="video/mp4" />
                  Your browser does not support the video tag.
                </video>
              ))}
            </div>
          </ScrollFade>


          <NiceJobReviews />
        </div>
      </section>

      <PaintBrushDivider variant="light" />

      {/* CTA Section */}
      <section className="py-20 md:py-32 bg-background border-t border-border">
        <div className="container text-center">
          <h2 className="text-3xl md:text-5xl font-light text-foreground mb-6">
            Ready for your skin transformation?
          </h2>
          <p className="text-muted-foreground text-lg mb-10 max-w-xl mx-auto">
            Book a consultation for a personalized treatment plan tailored to your skin's needs.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button asChild size="lg" className="rounded-full bg-primary hover:bg-primary/90 text-primary-foreground px-8">
              <a href="tel:+18472830204" onClick={trackCallEvent}>
                <Phone className="w-5 h-5 mr-2" />
                Call (847) 283-0204
              </a>
            </Button>
            <Button asChild variant="outline" size="lg" className="rounded-full border-border text-foreground hover:bg-primary hover:text-primary-foreground">
              <Link to="/contact">
                Contact Us
              </Link>
            </Button>
          </div>
        </div>
      </section>
    </Layout>;
};
export default Index;
